import React, { useEffect, useState, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.css';
import { Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import Spinner from '../../../shared/spinner';

import { GerenteDTO } from '../../../shared/dto/gerenteDTO';
import { MatrizDTO } from '../../../shared/dto/matrizDTO';
import { PromotoraDTO } from '../../../shared/dto/promotoraDTO';

import { ButtonAlterar, ButtonInativar, ButtonAtivar } from '../../../shared/buttons';

import SupervisorService from '../../../services/supervisorService';
import MatrizService from '../../../services/matrizService';
import PromotoraService from '../../../services/promotoraService';
import ImportacaoService from '../../../services/importacaoService'; // <--- Importado

import ConfirmModal from '../../../shared/customAlertConfirm';
import { useVerificaPermissao } from '../../../shared/useVerificaPermissao';
import ResponsivePagination from 'react-responsive-pagination';
import { faFilter, faRefresh, faFileImport, faDownload, faTimes } from '@fortawesome/free-solid-svg-icons'; // <--- Novos ícones
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Promotora } from '../../../shared/model/promotora';
import { useSlug } from '../../hooks/UseSlug';
import { ProtectedAction, ProtectedActionInterno } from '../../../AccessControl';

interface GerenteListDTO extends GerenteDTO {
  nomesRegionais?: string;
  nomesComerciais?: string;
  pontoVenda?: string | number;
  nomePontoVenda?: string;
}

function SupervisorComponent() {
  useVerificaPermissao();
  const navigate = useNavigate();
  const slug = useSlug(); // 2. Obtendo o slug


  // Services
  const supervisorService: SupervisorService = new SupervisorService();
  const matrizService = new MatrizService();
  const promotoraService = new PromotoraService();
  const importacaoService = new ImportacaoService(); // <--- Instância

  // States Principais
  const [loading, setLoading] = useState(true);
  const [gerente, setGerente] = useState<GerenteListDTO[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState<{ id: any; ativo: boolean } | null>(null);

  // Controle de Painéis
  const [filtros, setFiltros] = useState(false);
  const [importacaoOpen, setImportacaoOpen] = useState(false); // <--- State do Painel Importação

  // Filtros States
  const [matrizFiltro, setMatrizFiltro] = useState('');
  const [promotoraFiltro, setPromotoraFiltro] = useState('');
  const [codigoFiltro, setCodigoFiltro] = useState('');
  const [nomeFiltro, setNomeFiltro] = useState('');
  const [cidadeFiltro, setCidadeFiltro] = useState('');
  const [statusOrigemFiltro, setStatusOrigemFiltro] = useState('');

  const [matrizes, setMatrizes] = useState<MatrizDTO[]>([]);
  const [promotoras, setPromotoras] = useState<PromotoraDTO[]>([]);

  // States de Importação
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importMessage, setImportMessage] = useState<{ type: 'success' | 'danger', text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Paginação
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // --- Toggle Painéis ---
  const toggleFiltros = () => {
    setFiltros(!filtros);
    setImportacaoOpen(false); // Fecha importação ao abrir filtros
  };

  const toggleImportacao = () => {
    setImportacaoOpen(!importacaoOpen);
    setFiltros(false); // Fecha filtros ao abrir importação
    setImportMessage(null);
  };

  // --- Listagem ---
  const listarTodosGerentes = async (pagina = 0, size = 10) => {
    try {
      setLoading(true);

      const filtrosRequest: any = {
        page: pagina,
        size: size
      };

      if (matrizFiltro) filtrosRequest.matriz = matrizFiltro;
      if (promotoraFiltro) filtrosRequest.promotora = promotoraFiltro;
      if (codigoFiltro) filtrosRequest.codigo = codigoFiltro;
      if (nomeFiltro) filtrosRequest.nome = nomeFiltro;
      if (cidadeFiltro) filtrosRequest.cidade = cidadeFiltro;
      if (statusOrigemFiltro) filtrosRequest.ativo = statusOrigemFiltro;

      const response = await supervisorService.listarTodosGerentes(pagina, size, filtrosRequest);
      const data = response.data;

      setGerente(data.content || []);
      setPage(data.number + 1);
      setTotalPages(data.totalPages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onPageChange = (novaPagina: number) => {
    listarTodosGerentes(novaPagina - 1);
  };

  const ativarInativarGerente = async (id: any, ativo: boolean) => {
    setCurrentAction({ id, ativo });
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (currentAction) {
      const { id } = currentAction;
      try {
        await supervisorService.ativarInativarGerente(id);
        listarTodosGerentes();
      } catch (err) {
        console.error(err);
      } finally {
        setConfirmOpen(false);
        setCurrentAction(null);
      }
    }
  };

  // --- LÓGICA DE IMPORTAÇÃO ---
  const handleDownloadModelo = async () => {
    try {
      // Endpoint espera 'GERENTE' (mapeamento do Backend)
      const response = await importacaoService.baixarModelo('GERENTE');

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'modelo_importacao_supervisor.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (error) {
      console.error("Erro ao baixar modelo", error);
      setImportMessage({ type: 'danger', text: 'Erro ao baixar o modelo.' });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImportMessage(null);
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const extension = file.name.split('.').pop()?.toLowerCase();

      if (extension !== 'xlsx' && extension !== 'csv') {
        setImportMessage({ type: 'danger', text: 'Formato inválido. Apenas .xlsx e .csv são permitidos.' });
        setFileToUpload(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      setFileToUpload(file);
    }
  };

  const handleImportar = async () => {
    if (!fileToUpload) {
      setImportMessage({ type: 'danger', text: 'Selecione um arquivo para importar.' });
      return;
    }

    try {
      setImportLoading(true);
      // Chama o endpoint de importação 'GERENTE'
      await importacaoService.importar('GERENTE', fileToUpload);

      setImportMessage({ type: 'success', text: 'Importação realizada com sucesso!' });
      setFileToUpload(null);
      if (fileInputRef.current) fileInputRef.current.value = "";

      // Recarrega lista
      listarTodosGerentes(0);

      // Fecha painel após sucesso
      setTimeout(() => {
        setImportacaoOpen(false);
        setImportMessage(null);
      }, 3000);

    } catch (error: any) {
      console.error("Erro na importação", error);
      const msg = error.response?.data || 'Erro ao processar importação.';
      setImportMessage({ type: 'danger', text: msg });
    } finally {
      setImportLoading(false);
    }
  };

  // --- Efeitos ---
  useEffect(() => {
    listarTodosGerentes(0);
  }, []);

  useEffect(() => {
    const carregarMatrizes = async () => {
      try {
        const response = await matrizService.listarTodasMatrizesAtivas();
        setMatrizes(response.data || []);
      } catch (error) {
        console.error("Erro ao carregar matrizes:", error);
      }
    };
    carregarMatrizes();
  }, []);

  useEffect(() => {
    const carregarPromotorasPorMatriz = async () => {
      if (matrizFiltro) {
        try {
          const filtros = { matriz: matrizFiltro };
          const response = await promotoraService.listarTodasPromotoras(0, 999999, filtros);
          const data = response.data;
          setPromotoras(Array.isArray(data.content) ? data.content : []);
        } catch (error) {
          console.error("Erro ao carregar promotoras:", error);
          setPromotoras([]);
        }
      } else {
        setPromotoras([]);
        setPromotoraFiltro('');
      }
    };
    carregarPromotorasPorMatriz();
  }, [matrizFiltro]);

  return (
    <>
      <div className="position-relative m-md-3">
        <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h5>Origens &gt; Supervisor</h5>
          <div style={{ display: 'flex', gap: '10px' }}>
            {/* Botão Filtros */}
            <Button id="App-button-cor-principal-escura" onClick={toggleFiltros} active={filtros}>
              <FontAwesomeIcon icon={faFilter} style={{ marginRight: '10px' }} />
              Filtros
            </Button>

            {/* Botão Importar */}
            <ProtectedActionInterno>
              <Button variant="outline-primary" onClick={toggleImportacao} active={importacaoOpen}>
                <FontAwesomeIcon icon={faFileImport} style={{ marginRight: '10px' }} />
                Importar
              </Button>
            </ProtectedActionInterno>
            <ProtectedAction>
              <Button
                onClick={() => navigate(`${slug ? `/${slug}` : ''}/origens-supervisor/inclusao`, { state: { id: null } })}
                id="App-button-cor-principal-escura"
              >
                + Novo Supervisor
              </Button>
            </ProtectedAction>
          </div>
        </div>

        {/* --- ÁREA DE IMPORTAÇÃO --- */}
        {importacaoOpen && (
          <div className="card card-body mb-4" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
            <h6 className="card-title mb-3">
              <FontAwesomeIcon icon={faFileImport} className="me-2" />
              Importação em Lote
            </h6>
            <Row className="align-items-end g-3">
              <Col md={3}>
                <Form.Label>Passo 1: Modelo</Form.Label>
                <Button variant="outline-secondary" className="w-100" onClick={handleDownloadModelo}>
                  <FontAwesomeIcon icon={faDownload} className="me-2" />
                  Baixar Modelo
                </Button>
              </Col>

              <Col md={5}>
                <Form.Group controlId="formFile">
                  <Form.Label>Passo 2: Selecione o Arquivo (.xlsx)</Form.Label>
                  <Form.Control
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept=".xlsx, .csv"
                  />
                </Form.Group>
              </Col>

              <Col md={4} className="d-flex gap-2">
                <Button
                  id="App-button-cor-principal-escura"
                  className="flex-grow-1"
                  onClick={handleImportar}
                  disabled={!fileToUpload || importLoading}
                >
                  {importLoading ? <Spinner loading={true} /> : 'Confirmar Importação'}
                </Button>
                <Button variant="secondary" onClick={() => {
                  setImportacaoOpen(false);
                  setImportMessage(null);
                  setFileToUpload(null);
                }}>
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
              </Col>
            </Row>

            {importMessage && (
              <Alert variant={importMessage.type} className="mt-3 mb-0">
                {importMessage.text}
              </Alert>
            )}
          </div>
        )}

        {/* --- ÁREA DE FILTROS --- */}
        {filtros ?
          <>
            <Row className="mb-4" style={{ marginTop: '15px' }}>
              <Form.Group as={Col} md='2' controlId="formSelectMatriz">
                <Form.Label>Matriz</Form.Label>
                <Form.Select value={matrizFiltro} onChange={(e) => setMatrizFiltro(e.target.value)}>
                  <option value="">Selecione</option>
                  {matrizes.map((matriz) => (
                    <option key={matriz.id} value={String(matriz.id)}>
                      {matriz.id} - {matriz.nome}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group as={Col} md='2' controlId="formSelectPromotora">
                <Form.Label>Promotora</Form.Label>
                <Form.Select value={promotoraFiltro} onChange={(e) => setPromotoraFiltro(e.target.value)}>
                  <option value="">Selecione</option>
                  {promotoras.map((promotora) => (
                    <option key={promotora.id} value={String(promotora.id)}>
                      {promotora.id} - {promotora.nome}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group as={Col} md='2' controlId="formBasicSelect">
                <Form.Label>Código </Form.Label>
                <input type="text" className="form-control" value={codigoFiltro} onChange={(e) => setCodigoFiltro(e.target.value)} />
              </Form.Group>
              <Form.Group as={Col} md='4' controlId="formBasicSelect">
                <Form.Label>Nome</Form.Label>
                <input type="text" className="form-control" value={nomeFiltro} onChange={(e) => setNomeFiltro(e.target.value)} />
              </Form.Group>
              <Form.Group as={Col} md='2' controlId="formBasicSelect">
                <Form.Label>Cidade </Form.Label>
                <input type="text" className="form-control" value={cidadeFiltro} onChange={(e) => setCidadeFiltro(e.target.value)} />
              </Form.Group>
              <Form.Group as={Col} md='4' controlId="formSelectStatusOrigem">
                <Form.Label>Status Origem</Form.Label>
                <Form.Select
                  value={statusOrigemFiltro}
                  onChange={(e) => setStatusOrigemFiltro(e.target.value)}
                >
                  <option value="">Todas</option>
                  <option value="true">Ativas</option>
                  <option value="false">Inativas</option>
                </Form.Select>
              </Form.Group>
            </Row>
            <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <Button id="App-button-cor-principal-escura" onClick={() => listarTodosGerentes(0)}>
                <FontAwesomeIcon icon={faFilter} style={{ marginRight: '5px' }} />
                Aplicar
              </Button>
              <Button variant="secondary" onClick={() => {
                setMatrizFiltro(''); setPromotoraFiltro(''); setCodigoFiltro('');
                setNomeFiltro(''); setCidadeFiltro(''); setStatusOrigemFiltro('');
                listarTodosGerentes(0);
              }}>
                <FontAwesomeIcon icon={faRefresh} style={{ marginRight: '5px' }} />
                Limpar
              </Button>
            </div>
          </>
          : null}

        {loading ? <Spinner loading={loading} /> : (
          <div className="table-responsive">
            <div>
              <table className="table table-bordered table-striped">
                <thead>
                  <tr>
                    <th scope="col" id="App-header-table">Supervisor</th>
                    <th scope="col" id="App-header-table">CPF</th>
                    <th scope="col" id="App-header-table">Telefone</th>
                    <th scope="col" id="App-header-table">Promotora</th>
                    <th scope="col" id="App-header-table">Regional</th>
                    <th scope="col" id="App-header-table">Comercial</th>
                    <th scope="col" id="App-header-table">Ponto de Venda</th>
                    <ProtectedAction>
                      <th scope="col" id="App-header-table">Alteração</th>
                      <th scope="col" id="App-header-table">Status</th>
                    </ProtectedAction>
                  </tr>
                </thead>
                <tbody>
                  {gerente.map((item) => (
                    <tr key={item.id}>
                      <td>{item.id} - {item.nome}</td>
                      <td>{item.cpf}</td>
                      <td>{item.telefoneComercial}</td>
                      <td>{item.nomePromotora}</td>
                      <td>{item.nomesRegionais || '-'}</td>
                      <td>{item.nomesComerciais || '-'}</td>
                      <td>
                        {(item.pontoVenda && item.nomePontoVenda)
                          ? `${item.pontoVenda} - ${item.nomePontoVenda}`
                          : '-'
                        }
                      </td>
                      <ProtectedAction>
                        <td>
                          {/* 4. Link com Slug */}
                          <Link to={`${slug ? `/${slug}` : ''}/origens-supervisor/alteracao`} state={{ id: item.id }}>
                            <ButtonAlterar />
                          </Link>
                        </td>
                        <td>
                          {item.ativo ?
                            <ButtonInativar onClick={() => ativarInativarGerente(item.id, item.ativo)} />
                            :
                            <ButtonAtivar onClick={() => ativarInativarGerente(item.id, item.ativo)} />
                          }
                        </td>
                      </ProtectedAction>
                    </tr>
                  ))}
                </tbody>
              </table>

              <ResponsivePagination
                current={page}
                total={totalPages}
                onPageChange={onPageChange}
              />

              {confirmOpen && (<ConfirmModal message={currentAction?.ativo ? 'Tem certeza que deseja inativar este item?' : 'Tem certeza que deseja ativar este item?'} onConfirm={handleConfirm} onCancel={() => { setConfirmOpen(false); setCurrentAction(null) }} />)}
            </div>
          </div>
        )}
      </div >
    </>
  );
}

export default SupervisorComponent;